//src/pages/ForumPublic (Forum)


import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../api/api";
import Spinner from "../components/common/Spinner";
import { useAuth } from "../contexts/AuthContext";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function ForumPublic() {
  const [params] = useSearchParams();
  const threadId = params.get("thread");
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [thread, setThread] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");

  // Edição
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // Zoom imagem e Nível de Zoom
  const [zoomImage, setZoomImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Modal de Exclusão
  const [deleteModal, setDeleteModal] = useState({ show: false, replyId: null });

  useEffect(() => {
    if (!threadId) return;

    async function loadThread() {
      try {
        const res = await api.get(`/forum/thread/${threadId}`);
        setThread(res.data.thread);
        setAttachments(res.data.attachments || []);
        setReplies(res.data.replies || []);
      } catch (err) {
        console.error("Erro ao carregar tópico", err);
      } finally {
        setLoading(false);
      }
    }

    loadThread();
  }, [threadId]);

  const renderContent = (content) => {
    if (!content) return "";
    if (typeof content === "object") return content.value;
    try {
      const parsed = JSON.parse(content);
      return parsed?.value || content;
    } catch {
      return content;
    }
  };

  const canEdit = (reply) => {
    if (!user) return false;
    return user.role === "admin" || reply.user_id === user.id;
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);

    try {
      await api.post(`/forum/thread/${threadId}/reply`, {
        content: replyText,
      });

      setReplyText("");
      const res = await api.get(`/forum/thread/${threadId}`);
      setReplies(res.data.replies || []);
    } catch (err) {
      console.error("Erro ao enviar resposta", err);
    } finally {
      setSending(false);
    }
  };

  const handleSaveEdit = async (replyId) => {
    if (!editingText.trim()) return;

    try {
      await api.put(`/forum/reply/${replyId}`, {
        content: editingText,
      });

      setReplies((prev) =>
        prev.map((r) => (r.id === replyId ? { ...r, content: editingText } : r))
      );

      setEditingReplyId(null);
      setEditingText("");
    } catch (err) {
      console.error("Erro ao editar resposta", err);
    }
  };

  const openDeleteModal = (replyId) => {
    setDeleteModal({ show: true, replyId });
  };

  const handleConfirmDelete = async () => {
    const { replyId } = deleteModal;
    if (!replyId) return;

    try {
      await api.delete(`/forum/reply/${replyId}`);
      setReplies((prev) => prev.filter((r) => r.id !== replyId));
      setDeleteModal({ show: false, replyId: null });
    } catch (err) {
      console.error("Erro ao excluir resposta", err);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Spinner />
      </div>
    );
  }

  if (!thread) {
    return <div className="py-20 text-center opacity-60">Tópico não encontrado.</div>;
  }

  return (
    <div className="container mx-auto px-6 py-16 max-w-4xl">
      <Link to="/" className="text-sm text-indigo-500 hover:underline">
        ← Voltar para Home
      </Link>

      <h1 className="text-3xl font-extrabold mt-4 mb-1">{thread.title}</h1>

      <div className="text-xs text-gray-500 mb-8">
        Publicado em {format(new Date(thread.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
      </div>

      <div className="bg-white rounded-2xl shadow p-6 mb-6 whitespace-pre-line text-gray-800 border border-gray-100">
        {renderContent(thread.content)}
      </div>

      {/* ANEXOS */}
      {attachments.length > 0 && (
        <div className="mb-6">
          <h3 className="font-bold mb-3">📎 Anexos</h3>
          <div className="flex flex-col gap-4 items-center sm:items-start">
            {attachments.map((a) => {
              const url = a.url;
              const isImage = a.mime?.startsWith("image");

              if (isImage) {
                return (
                  <div key={a.id} className="relative group cursor-zoom-in w-full max-w-[800px]">
                    <img
                      src={url}
                      alt={a.filename}
                      onClick={() => {
                        setZoomImage(url);
                        setZoomLevel(1);
                      }}
                      className="rounded-xl border shadow-sm hover:shadow-md transition-all h-[400px] w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition rounded-xl pointer-events-none flex items-center justify-center">
                      <span className="text-white text-xl">🔍</span>
                    </div>
                  </div>
                );
              }

              if (a.mime === "application/pdf") {
                return (
                  <a
                    key={a.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center justify-center p-4 border rounded-xl hover:bg-gray-50 transition w-full max-w-[200px]"
                  >
                    <span className="text-3xl mb-1">📄</span>
                    <span className="text-[10px] font-semibold truncate w-full text-center">
                      {a.filename}
                    </span>
                  </a>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* RESPOSTAS */}
      <div className="mb-12">
        <h3 className="font-bold mb-4">💬 Respostas ({replies.length})</h3>
        <div className="space-y-4 flex flex-col">
          {replies.map((r) => {
            // Verifica se o autor é Admin, Supervisor ou Professor para alinhar à direita
            const isStaff = ["admin", "supervisor", "professor"].includes(r.author_role?.toLowerCase());

            return (
              <div
                key={r.id}
                className={`relative group p-4 rounded-2xl border transition-all max-w-[85%] sm:max-w-[75%] 
                  ${isStaff 
                    ? "self-end bg-indigo-600 text-white border-indigo-500 rounded-tr-none shadow-indigo-100" 
                    : "self-start bg-gray-50 text-gray-800 border-gray-200 rounded-tl-none shadow-sm"
                  }`}
              >
                <div className="flex justify-between items-center mb-2 gap-4">
                  <span className={`font-bold text-xs uppercase tracking-wider ${isStaff ? "text-indigo-200" : "text-indigo-600"}`}>
                    {r.author || "Usuário"}
                    {isStaff && (
                      <span className="ml-2 text-[9px] bg-white/20 px-2 py-0.5 rounded text-white">
                        {r.author_role?.toUpperCase()} 🛡️
                      </span>
                    )}
                  </span>

                  {canEdit(r) && (
                    <div className="opacity-0 group-hover:opacity-100 transition flex gap-3 text-xs">
                      <button
                        className="hover:scale-125 transition"
                        onClick={() => {
                          setEditingReplyId(r.id);
                          setEditingText(renderContent(r.content));
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        className="hover:scale-125 transition"
                        onClick={() => openDeleteModal(r.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>

                {editingReplyId === r.id ? (
                  <div className="space-y-2">
                    <textarea
                      rows={3}
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="w-full border rounded-lg p-2 text-sm text-gray-800 outline-none"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => handleSaveEdit(r.id)} className="px-4 py-1 bg-green-500 text-white rounded-lg text-sm font-bold">Salvar</button>
                      <button onClick={() => setEditingReplyId(null)} className="px-4 py-1 bg-gray-300 text-gray-700 rounded-lg text-sm">Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div className={`text-sm whitespace-pre-line leading-relaxed ${isStaff ? "text-white" : "text-gray-700"}`}>
                    {renderContent(r.content)}
                  </div>
                )}
                <div className={`text-[9px] mt-2 text-right opacity-60 ${isStaff ? "text-white" : "text-gray-500"}`}>
                   {r.created_at && format(new Date(r.created_at), "HH:mm")}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* NOVA RESPOSTA */}
      {user ? (
        <div className="bg-white border rounded-2xl p-6 shadow-sm">
          <h4 className="font-bold mb-3">✍️ Sua resposta</h4>
          <textarea
            rows={4}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            className="w-full border border-gray-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-indigo-200 outline-none transition"
            placeholder="Compartilhe seu conhecimento ou dúvida..."
          />
          <button
            onClick={handleSendReply}
            disabled={sending || !replyText.trim()}
            className="mt-4 px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {sending ? "Enviando..." : "Enviar resposta"}
          </button>
        </div>
      ) : (
        <div className="bg-indigo-50 text-indigo-700 p-6 rounded-2xl text-center font-medium">
          Faça login para participar desta discussão.
        </div>
      )}

      {/* MODAL ZOOM IMAGEM */}
      {zoomImage && (
        <div className="fixed inset-0 bg-black/95 z-[999] flex flex-col items-center justify-center animate-fadeIn p-4" onClick={() => setZoomImage(null)}>
          <button className="fixed top-6 right-6 text-white bg-white/10 hover:bg-red-600 w-12 h-12 rounded-full flex items-center justify-center transition-all border border-white/20 z-[1000]" onClick={() => setZoomImage(null)}>
            <span>✕</span>
          </button>
          <div className="relative w-full h-full flex items-center justify-center overflow-auto p-10" onClick={(e) => e.stopPropagation()}>
            <img src={zoomImage} alt="Zoom" className="rounded-lg shadow-2xl animate-scaleUp object-contain transition-transform duration-200 ease-out" style={{ transform: `scale(${zoomLevel})`, maxWidth: '85%', maxHeight: '80vh' }} />
          </div>
          <div className="fixed bottom-10 w-full max-w-xs bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/20 flex flex-col gap-2 items-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between w-full text-white text-[10px] font-bold opacity-70">
              <span>Mín</span><span>{Math.round(zoomLevel * 100)}%</span><span>Máx</span>
            </div>
            <input type="range" min="1" max="4" step="0.1" value={zoomLevel} onChange={(e) => setZoomLevel(parseFloat(e.target.value))} className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500" />
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO (FORA DO MODAL DE ZOOM) */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-black/60 z-[2000] flex items-center justify-center animate-fadeIn p-4">
          <div className="bg-white rounded-2xl p-6 shadow-2xl w-full max-w-sm transform animate-scaleUp">
            <div className="text-center">
              <div className="bg-red-100 text-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">⚠️</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Excluir Mensagem</h3>
              <p className="text-gray-500 text-sm mb-6">Tem certeza que deseja apagar esta resposta? Esta ação não pode ser desfeita.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal({ show: false, replyId: null })} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition">Cancelar</button>
              <button onClick={handleConfirmDelete} className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition shadow-lg">Excluir</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleUp { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-scaleUp { animation: scaleUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        input[type='range']::-webkit-slider-thumb { appearance: none; width: 16px; height: 16px; background: #6366f1; border-radius: 50%; cursor: pointer; border: 2px solid white; }
      `}</style>
    </div>
  );
}